import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Search } from "lucide-react";
import logoRed from "@/assets/bunadstuggu-logo-red.png";
import logoWhite from "@/assets/bunadstuggu-logo-white.png";
import { bunader } from "@/data/bunader";
import SearchDialog from "@/components/SearchDialog";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDrakterOpen, setIsDrakterOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#aktuelt", label: "Aktuelt" },
    { href: "#drakter", label: "Våre drakter", hasDropdown: true },
    { href: "#historie", label: "Historie" },
    { href: "#kontakt", label: "Kontakt" },
  ];

  const handleNavClick = (link: { href: string; isRoute?: boolean }) => {
    if (link.isRoute) {
      navigate(link.href);
    } else if (link.href.startsWith("#") && location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.querySelector(link.href);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };


  return (
    <>
      <header 
        role="banner"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? "bg-background shadow-soft" 
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20 lg:h-24">
            <Link to="/" className="block">
              <img 
                src={isScrolled ? logoRed : logoWhite} 
                alt="Bunadstuggu" 
                className="h-10 lg:h-14 w-auto transition-all duration-500 drop-shadow-lg"
                fetchPriority="high"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav aria-label="Hovednavigasjon" className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                link.hasDropdown ? (
                  <div 
                    key={link.href}
                    className="relative"
                    onMouseEnter={() => setIsDrakterOpen(true)}
                    onMouseLeave={() => setIsDrakterOpen(false)}
                  >
                    <a
                      href={link.href}
                      className={`flex items-center gap-1 transition-colors font-sans text-xs tracking-[0.2em] uppercase ${
                        isScrolled 
                          ? "text-foreground/70 hover:text-foreground" 
                          : "text-white/90 hover:text-white"
                      }`}
                    >
                      {link.label}
                      <ChevronDown className={`w-3 h-3 transition-transform ${isDrakterOpen ? "rotate-180" : ""}`} />
                    </a>
                    
                    {/* Dropdown Menu */}
                    {isDrakterOpen && (
                      <div className="absolute top-full left-0 pt-2 animate-fade-in">
                        <div className="bg-background rounded-lg shadow-lg border border-border py-2 min-w-[200px]">
                          {bunader.map((bunad) => (
                            <Link
                              key={bunad.id}
                              to={`/drakt/${bunad.id}`}
                              className="block px-4 py-2 text-sm text-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
                            >
                              {bunad.title}
                            </Link>
                          ))}
                          <div className="border-t border-border my-1" />
                          <Link
                            to="/tilbehor"
                            className="block px-4 py-2 text-sm text-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
                          >
                            Tilbehør
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => handleNavClick(link)}
                    className={`transition-colors font-sans text-xs tracking-[0.2em] uppercase ${
                      isScrolled 
                        ? "text-foreground/70 hover:text-foreground" 
                        : "text-white/90 hover:text-white"
                    }`}
                  >
                    {link.label}
                  </a>
                )
              ))}
              
              {/* Search Icon */}
              <button
                onClick={() => setIsSearchOpen(true)}
                className={`p-2 transition-colors ${
                  isScrolled 
                    ? "text-foreground/70 hover:text-foreground" 
                    : "text-white/90 hover:text-white"
                }`}
                aria-label="Søk"
              >
                <Search className="w-4 h-4" />
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <div className="flex items-center gap-2 lg:hidden">
              <button
                onClick={() => setIsSearchOpen(true)}
                className={`p-2 transition-colors ${isScrolled ? "text-foreground" : "text-white"}`}
                aria-label="Søk"
              >
                <Search className="w-5 h-5" />
              </button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`p-2 transition-colors ${isScrolled ? "text-foreground" : "text-white"}`}
                aria-label={isMenuOpen ? "Lukk meny" : "Åpne meny"}
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="lg:hidden py-6 bg-background border-t border-border -mx-6 px-6 animate-fade-in">
              <nav className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  link.hasDropdown ? (
                    <div key={link.href}>
                      <button
                        onClick={() => setIsDrakterOpen(!isDrakterOpen)}
                        className="flex items-center gap-2 text-foreground/70 hover:text-foreground transition-colors font-sans text-sm tracking-widest uppercase py-2 w-full"
                      >
                        {link.label}
                        <ChevronDown className={`w-4 h-4 transition-transform ${isDrakterOpen ? "rotate-180" : ""}`} />
                      </button>
                      {isDrakterOpen && (
                        <div className="pl-4 flex flex-col gap-2 mt-2">
                          {bunader.map((bunad) => (
                            <Link
                              key={bunad.id}
                              to={`/drakt/${bunad.id}`}
                              onClick={() => setIsMenuOpen(false)}
                              className="text-foreground/60 hover:text-foreground transition-colors text-sm py-1"
                            >
                              {bunad.title}
                            </Link>
                          ))}
                          <div className="border-t border-border my-1" />
                          <Link
                            to="/tilbehor"
                            onClick={() => setIsMenuOpen(false)}
                            className="text-foreground/60 hover:text-foreground transition-colors text-sm py-1"
                          >
                            Tilbehør
                          </Link>
                        </div>
                      )}
                    </div>
                  ) : (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={() => {
                        handleNavClick(link);
                        setIsMenuOpen(false);
                      }}
                      className="text-foreground/70 hover:text-foreground transition-colors font-sans text-sm tracking-widest uppercase py-2"
                    >
                      {link.label}
                    </a>
                  )
                ))}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Search Dialog */}
      <SearchDialog open={isSearchOpen} onOpenChange={setIsSearchOpen} />
    </>
  );
};

export default Header;
